import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Image, Pressable, Text, View } from 'react-native';
import UserInformationModal from './UserInformationModal';
import GlobalSearchModal, { SystemPage } from './GlobalSearchFIlter';
import { applicationPages, RootNavigation } from '../Routes';
import { WindowInformation } from '../services/window-information';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faList, faUser, faSearch } from '@fortawesome/free-solid-svg-icons';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { createStyleSheet, UnistylesRuntime, useStyles } from 'react-native-unistyles';
import { useAuth } from '../context/Auth';
import { NavigationProp } from '@react-navigation/native';
import { RootStackParamList } from '../typings/Params';
import { translate } from '../services/translate';
import { useCurrentApp } from '../context/CurrentApp';


interface Props {
    navigation: NavigationProp<RootStackParamList> | RootNavigation;
    setOpen?: React.Dispatch<React.SetStateAction<boolean>>;
    showDropDownOnMobile?: boolean;
}


function TopMenu({ navigation, setOpen, showDropDownOnMobile }: Props) {
    const { styles, theme } = useStyles(stylesheet);
    const [showDropDown, setShowDropDown] = useState(false);
    const [showSearch, setShowSearch] = useState(false);
    const windowInfo = WindowInformation();
    const isSmartSampa = useMemo(() => window.location.hostname.includes('smartsampa'), [window.location]);

    const { user: authenticatedUser } = useAuth();
    const currentApp = useCurrentApp();

    const handleGlobalKeyDown = useCallback((e: KeyboardEvent) => {
        if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
            e.preventDefault();
            setShowSearch(prev => !prev);
        }
    }, []);

    useEffect(() => {
        window.addEventListener('keydown', handleGlobalKeyDown);
        return () => window.removeEventListener('keydown', handleGlobalKeyDown);
    }, [handleGlobalKeyDown]);

    const accessiblePages = useMemo(() => {
        const pages: SystemPage[] = [];

        for (const moduleObj of Object.values(applicationPages.modules)) {
            if (moduleObj.canAccess(authenticatedUser, currentApp)) {
                for (const screenObj of Object.values(moduleObj.screens)) {
                    if (screenObj.accessInformation && screenObj.canAccess(authenticatedUser, currentApp)) {
                        pages.push({
                            key: `${moduleObj.label}-${screenObj.label}`,
                            label: translate(screenObj.label),
                            module: translate(moduleObj.label),
                            navigate: screenObj.accessInformation.navigate,
                            icon: screenObj.accessInformation.icon,
                        });
                    }
                }
            }
        }

        for (const screenObj of Object.values(applicationPages.screens)) {
            if (screenObj.accessInformation && screenObj.canAccess(authenticatedUser, currentApp)) {
                pages.push({
                    key: `${screenObj.label}`,
                    label: translate(screenObj.label),
                    navigate: screenObj.accessInformation.navigate,
                    icon: screenObj.accessInformation.icon,
                });
            }
        }

        return pages;
    }, [authenticatedUser]);

    const shortcutLabel = useMemo(() => {
        const isMac = navigator.platform.toUpperCase().includes('MAC');
        return isMac ? '⌘ K' : 'Ctrl K';
    }, []);

    return (
        <View style={styles.container}>
            <View style={styles.titleContainer}>
                {isSmartSampa ? (windowInfo.isMobileOrTablet
                    ? <Image style={styles.image(290)} source={require(`../../assets/big_smartsampa_logo_mobile_${UnistylesRuntime.themeName}.png`)} />
                    : <Image style={styles.image(400)} source={require(`../../assets/big_smartsampa_logo_${UnistylesRuntime.themeName}.png`)} />
                ) :
                    <Image style={styles.image(200)} source={require(`../../assets/SentinelX_logo_${UnistylesRuntime.themeName}.png`)} />
                }
            </View>

            {!windowInfo.isMobileOrTablet && (
                <Pressable style={styles.searchTrigger} onPress={() => setShowSearch(true)}>
                    <FontAwesomeIcon icon={faSearch as IconDefinition} fontSize={14} color={theme.colors.placeholder} />
                    <Text style={styles.searchPlaceholder}>{translate('typeToSearch')}</Text>
                    <View style={styles.shortcutBadge}>
                        <Text style={styles.shortcutText}>{shortcutLabel}</Text>
                    </View>
                </Pressable>
            )}
            {!windowInfo.isMobileOrTablet && (
                <View style={styles.separator} />
            )}
            <Pressable onPress={() => {
                if (windowInfo.isMobile && !showDropDownOnMobile) {
                    setOpen?.(old => !old);
                } else {
                    setShowDropDown(!showDropDown);
                }
            }} style={styles.userContainer}>
                <>
                    {windowInfo.isMobile ?
                        showDropDownOnMobile ?
                            <View style={styles.userFrame}>
                                <FontAwesomeIcon icon={faUser as IconDefinition} color='black' stroke='1.5' />
                            </View>
                            : <FontAwesomeIcon icon={faList as IconDefinition} color={theme.colors.iconColor} size="xl" stroke='1.5' />
                        :
                        <>
                            <Text style={styles.title}>{authenticatedUser?.name}</Text>
                            <View style={styles.userFrame}>
                                <FontAwesomeIcon icon={faUser as IconDefinition} color='black' stroke='1.5' />
                            </View>
                        </>
                    }
                </>
            </Pressable>

            <UserInformationModal
                showDropDown={showDropDown}
                setShowDropDown={setShowDropDown}
                navigation={navigation}
            />

            <GlobalSearchModal
                visible={showSearch}
                onClose={() => setShowSearch(false)}
                navigation={navigation}
                pages={accessiblePages}
            />
        </View>
    );
}


const stylesheet = createStyleSheet((theme) => ({
    container: {
        backgroundColor: theme.colors.topMenu,
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        alignContent: 'center',
        height: '100%',
        width: '100%',
    },

    titleContainer: {
        flex: 1,
        flexDirection: 'row',
        alignItems: 'center',
        marginLeft: 15,
    },

    searchTrigger: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        backgroundColor: theme.colors.field?.background ?? 'rgba(255,255,255,0.06)',
        borderWidth: 1,
        borderColor: theme.colors.field?.border ?? '#444',
        borderRadius: 6,
        paddingHorizontal: 12,
        height: 36,
        width: 360,
        cursor: 'text',
    },

    searchPlaceholder: {
        flex: 1,
        fontSize: 14,
        color: theme.colors.placeholder,
        fontFamily: 'Open Sans',
        userSelect: 'none',
    },

    shortcutBadge: {
        borderWidth: 1,
        borderColor: theme.colors.field?.border ?? '#555',
        borderRadius: 4,
        paddingHorizontal: 5,
        paddingVertical: 1,
    },

    shortcutText: {
        fontSize: 11,
        color: theme.colors.placeholder,
        fontFamily: 'Open Sans',
    },

    title: {
        color: '#717171',
        fontSize: 16,
    },

    userContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: 10,
        paddingVertical: 15,
        paddingRight: 15,
    },

    userFrame: {
        width: 32,
        aspectRatio: 1,
        borderRadius: 50,
        backgroundColor: '#CCCCCC',
        justifyContent: 'center',
        alignItems: 'center',
    },

    image: (width) => ({
        height: 45,
        width,
        resizeMode: 'contain',
    }),

    separator: {
        width: 1,
        height: 20,
        backgroundColor: theme.colors.field?.border ?? '#444',
        marginHorizontal: 24,
    },
}));

export default TopMenu;
